import React, { useState } from 'react';
import { Box, Text } from 'rebass/styled-components';
import { observer } from 'mobx-react';
import styled from 'styled-components';

import { StyledLink } from 'shared/components';
import { FormattedInputWithButton } from 'shared/components/text-input-with-label';
import useStores from 'shared/hooks/use-stores';
import { GqlDispensaries } from 'types/graphql';

import { useApplyCouponCode } from 'src/state/actions/cart';

import { useAutoApplyPromoCode } from './use-auto-apply-promo-code';

type CartStore = {
  order: {
    dispensary: Partial<GqlDispensaries> | undefined;
  };
  coupon: { code: string } | null;
  removeCoupon: () => void;
  rewardsProgramDisplayName: string;
  isCalculating: boolean;
  orderSubtotal: number;
};

function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength - 3)}...`;
}

function PromoCodeFieldV2(): JSX.Element {
  const { Cart } = useStores() as { Cart: CartStore };
  const { order, coupon, removeCoupon, rewardsProgramDisplayName, isCalculating, orderSubtotal } = Cart;
  const { dispensary } = order;
  const enableOmniauthLogin = dispensary?.storeSettings?.enableOmniauthLoginCheckout ?? false;
  const isCartReady = !isCalculating && orderSubtotal > 0;

  const [tempPromoCode, setTempPromoCode] = useState('');
  const [showPromoCodeField, setShowPromoCodeField] = useState(false);
  const applyCouponCode = useApplyCouponCode();

  useAutoApplyPromoCode(dispensary?.id, enableOmniauthLogin, isCartReady, rewardsProgramDisplayName);

  const toggleShowPromoField = (): void => setShowPromoCodeField(!showPromoCodeField);

  const onPromoSubmit = (tmpPromoCode: string, dispensaryId: string): void => {
    void applyCouponCode(tmpPromoCode, dispensaryId, false, rewardsProgramDisplayName);
    toggleShowPromoField();
  };

  if (coupon) {
    const couponCode = truncate(coupon.code, 16);
    return (
      <CouponAppliedContainer mb='23px'>
        <CouponCode title={coupon.code}>Promo Code: {couponCode}</CouponCode>
        <StyledLink as='button' fontSize='13px' onClick={removeCoupon} disabled={isCalculating}>
          Undo
        </StyledLink>
      </CouponAppliedContainer>
    );
  }

  if (!showPromoCodeField) {
    return (
      <Box mb='23px'>
        <StyledLink as='button' fontSize='13px' onClick={toggleShowPromoField} disabled={isCalculating}>
          Add a promo code
        </StyledLink>
      </Box>
    );
  }

  return (
    <FormattedInputWithButton
      mt='8px'
      mb='20px'
      id='promoCode'
      label='Enter promo code'
      buttonText='Apply'
      value={tempPromoCode}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTempPromoCode(event.target.value)}
      onSubmit={() => dispensary?.id && onPromoSubmit(tempPromoCode, dispensary.id)}
      disabled={isCalculating}
    />
  );
}

export default observer(PromoCodeFieldV2);

const CouponCode = styled(Text)`
  font-size: 13px;
  color: var(--dcm-checkout-summary-text-color, #606369);
`;

const CouponAppliedContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  border-top: 1px solid var(--dcm-checkout-summary-border-color, #d3dadf);
  padding: 20px 0 0 0;
`;
